import { getAuthToken } from "../renderer/platform/authStorage";
import { publishNetworkStatus } from "../renderer/platform/networkStatus";
import { APP_CONFIG } from "../utils/runtimeConfig";

const BASE_URL = APP_CONFIG.apiBaseUrl;

/**
 * A generic API request function.
 * @param {string} endpoint - The endpoint after /api/.
 * @param {object} options - Fetch options.
 * @param {string} options.method - GET, POST, etc.
 * @param {object} [options.body] - Request payload.
 * @param {boolean} [options.requiresAuth=true] - Whether to attach the token.
 * @returns {Promise<object>} - Parsed JSON data or throws error.
 */
const readResponseBody = async (response) => {
  if ([204, 205].includes(response.status)) return null;

  const text = await response.text();
  if (!text) return null;

  const contentType = response.headers.get("content-type")?.toLowerCase() || "";
  const claimsJson = contentType.includes("application/json") || contentType.includes("+json");
  try {
    return JSON.parse(text);
  } catch {
    if (response.ok && claimsJson) {
      const error = new Error("The API returned malformed JSON.");
      error.status = response.status;
      throw error;
    }
    return text;
  }
};

const apiCall = async (
  endpoint,
  { method = "GET", body = null, requiresAuth = true, signal } = {}
) => {
  if (!BASE_URL) throw new Error("The API endpoint is not configured.");
  const url = `${BASE_URL}${endpoint}`;
  const headers = { Accept: "application/json" };

  const hasBody = body !== null && body !== undefined;
  const isFormData = body instanceof FormData;

  if (hasBody && !isFormData) {
    headers["Content-Type"] = "application/json";
  }

  if (requiresAuth) {
    const token = getAuthToken();
    if (!token) throw new Error("Authentication token not found.");
    headers["Authorization"] = `Bearer ${token}`;
  }

  const config = {
    method,
    headers,
    body: hasBody ? isFormData ? body : JSON.stringify(body) : undefined,
    signal,
  };

  let response;
  try {
    response = await fetch(url, config);
    publishNetworkStatus(true);
  } catch (error) {
    if (error?.name !== "AbortError") publishNetworkStatus(false);
    throw error;
  }
  const data = await readResponseBody(response);

  if (!response.ok) {
    const responseMessage =
      data && typeof data === "object" && typeof data.message === "string"
        ? data.message
        : typeof data === "string" && data.trim()
          ? data.trim().slice(0, 500)
          : `API call failed with HTTP ${response.status}`;
    const error = new Error(responseMessage);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

export default apiCall;
